"use client";

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from "next/navigation";
import posthogClient from "posthog-js";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { getAuthErrorMessage, isAccountLinkingError, isKnownAuthErrorCode } from "@domainstack/auth/errors";

/**
 * Hook to handle auth callback error query parameters.
 *
 * Automatically:
 * - Shows toast notifications for errors with user-friendly messages
 * - Cleans up the `error` and `error_description` query params from the URL
 *
 * The URL is cleaned with `history.replaceState(null, …)` rather than a router navigation:
 * `/login` and `/settings/*` have intercepting routes, so a client-side navigation
 * would open a second modal on top of the full page. Passing `null` (not
 * `history.state`, which carries Next's `__NA` flag) lets Next sync `useSearchParams`,
 * so a later `router.refresh()` can't restore the error params.
 *
 * @example
 * // In login page (sign-in callbacks)
 * useAuthCallback();
 *
 * @example
 * // In settings (account linking callbacks)
 * useAuthCallback({ context: "link" });
 */
export function useAuthCallback(t0) {
  const $ = _c(6);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    context: t2
  } = t1;
  const context = t2 === undefined ? "sign_in" : t2;
  const searchParams = useSearchParams();
  const processedRef = useRef(false);
  let t3;
  let t4;
  if ($[2] !== context || $[3] !== searchParams) {
    t3 = () => {
      const error = searchParams.get("error");
      if (!error || processedRef.current) {
        return;
      }
      processedRef.current = true;
      const isLinkError = context === "link" || isAccountLinkingError(error);
      const code = isKnownAuthErrorCode(error) ? error : "unknown";
      posthogClient.captureException(new Error(`auth callback error: ${code}`), {
        action: context === "link" ? "link_account" : "sign_in",
        errorCode: code
      });
      toast.error(isLinkError ? "Failed to link account" : "Sign in failed", {
        description: getAuthErrorMessage(error)
      });
      const params = new URLSearchParams(searchParams.toString());
      params.delete("error");
      params.delete("error_description");
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
      window.history.replaceState(null, "", newUrl);
    };
    t4 = [context, searchParams];
    $[2] = context;
    $[3] = searchParams;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
}